<template>
  <v-container fluid class="text-xs-justify">
    <!-- intro ~*~*~*~*~*~*~*~*~*~*~*~*~*~* -->

    <CorePageIntro>
      <template slot="content">
        This administration page provides for peak settings configuration. If in
        manual mode, you may set any of the peak indicators as on or off. Those
        will drive the forecasts and calculations executed by Sustaine&trade;.
        If in auto mode, all manual settings will be ignored and Sustaine&trade;
        will use input from the ISO and utility to drive the calculations and
        forecasts.
      </template>
    </CorePageIntro>

    <v-card outlined>
      <v-card-text>
        <!-- top header / toolbar ~*~*~*~*~*~*~*~*~*~*~*~*~*~* -->
        <v-toolbar flat>
          <v-toolbar-title class="text-uppercase body-1 font-weight-medium">Coincident Peaks</v-toolbar-title>
          <v-divider class="mx-2" inset vertical></v-divider>
          <div class="flex-grow-1"></div>
        </v-toolbar>

        <v-divider class="my-2" inset></v-divider>

        <!-- form *~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*~ -->
          <form @submit.prevent="saveConfigs">
            <!-- manual | auto -->
            <v-row no-gutters>
                <v-col sm="1" md="1"></v-col>
                <v-col sm="11" md="9">
                    <h3 class="subheading font-weight-bold pb-1">Manual Override</h3>
                    <p v-if="peak_mode">
                        If <strong>MANUA</strong>L mode, you are in control, and can set all or none of the Coincident Peaks toggles used to determine calculate forecasts.
                    </p>
                    <p v-else>
                        If <strong>AUTO</strong>, you are allowing Sustaine&trade; to evaluate the marketplace and determine peak mode automatically. In AUTO mode, all manual Coincident Peaks overrides are disabled.
                    </p>
                    <p class="title font-weight-medium"><span class="yellow">Warning: This is a global setting. Changes here effect all facilities in the Sustaine system.</span></p>
                </v-col>
                <v-col sm="11" md="2" class="text-right">
                    <v-switch
                        v-model="peak_mode"
                        :label="`${translatePeakMode}`"
                        @change="checkManualOverrides"
                        v-if="!error"
                        color="orange darken-2">
                    </v-switch>
                    <h3 v-if="error" class="pt-2 red--text-darken-2">">ERROR!</h3>
                </v-col>
            </v-row>

            <!-- conditional block, enabled if manual override -->
            <div id="manual-overrides" v-if="!error">

                <v-divider inset></v-divider>

                <!-- 1CP *~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*~ -->
                <v-row no-gutters class="mt-2">
                    <v-col sm="1" md="1"></v-col>
                    <v-col sm="1" md="1"></v-col>
                    <v-col sm="1" md="1"><strong>MANUAL:</strong></v-col>
                    <v-col sm="9" md="7">
                        <h3 class="subheading font-weight-bold pb-1">1CP</h3>
                        <span class="caption">Utility: {{ getUtilityName }}</span>
                    </v-col>
                    <v-col sm="9" md="2" class="text-right">
                        <v-switch
                            v-model="utility_1CP"
                            :label="`${translate1CP}`"
                            color="orange darken-2">
                        >
                        </v-switch>
                    </v-col>
                </v-row>
                <!-- 5CP *~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*~ -->
                <v-row no-gutters>
                    <v-col sm="3" md="3"></v-col>
                    <v-col sm="9" md="7">
                        <h3 class="subheading font-weight-bold pb-1">5CP</h3>
                        <span class="caption">ISO: {{ getIsoName }}</span>
                    </v-col>
                    <v-col sm="9" md="2" class="text-right">
                        <v-switch
                            v-model="iso_5CP"
                            :label="`${translate5CP}`"
                            color="orange darken-2">
                        </v-switch>
                    </v-col>
                </v-row>

            </div>
            <!-- end manual overrides -->

            <div class="row text-center">
              <div class="col-md-12">
                <v-btn v-if="!error" color="primary" type="submit">Update</v-btn>
              </div>
            </div>
          </form>

        </v-card-text>            
      </v-card>

  </v-container>
</template>

<script>
import ConfigService from '@/services/ConfigService'
import { mapGetters } from 'vuex'
export default {
  name: 'CoincidentPeak',
  data() {
    return {
      utility_1CP: false, // utility  PEAK_DAY_FLAG_UTILITY_{utility_id} == 1CP
      iso_5CP: false, // iso      PEAK_DAY_FLAG_ISO_{iso_id} == 5CP
      peak_mode: '', // mode     PEAK_DAY_MODE == auto | manual  -- DO NO DEFAULT THIS
      error: false
    }
  },
  computed: {
    ...mapGetters([
      'getFacilityId',
      'getUtilityId',
      'getUtilityName',
      'getIsoId',
      'getIsoName'
    ]),
    translatePeakMode() {
      if (this.peak_mode === true) {
        return 'MANUAL'
      }
      return 'AUTO'
    },
    translate5CP() {
      if (this.iso_5CP === true) {
        return 'ON'
      }
      return 'OFF'
    },
    translate1CP() {
      if (this.utility_1CP === true) {
        return 'ON'
      }
      return 'OFF'
    }
  },
  created() {
    this.getModeConfig()
    this.getUtilityConfig()
    this.getIsoConfig()
  },
  methods: {
    checkManualOverrides() {
      if (this.peak_mode === true) {
        document.getElementById('manual-overrides').style.display = 'block' // manual
      }
      if (this.peak_mode === false) {
        document.getElementById('manual-overrides').style.display = 'none' // auto
      }
    },
    getUtilityConfig() {
      let key = 'PEAK_DAY_FLAG_UTILITY_' + this.getUtilityId
      ConfigService.getValue(key)
        .then(response => {
          this.utility_1CP = response.data.value == 'true'
        })
        .catch(error => {
          this.postConfig('UTILITY', this.getUtilityId)
        })
    },
    getIsoConfig() {
      let key = 'PEAK_DAY_FLAG_ISO_' + this.getIsoId
      ConfigService.getValue(key)
        .then(response => {
          this.iso_5CP = response.data.value == 'true'
        })
        .catch(error => {
          this.postConfig('ISO', this.getIsoId)
        })
    },
    saveConfigs() {
      if (this.peak_mode === true) {
        // only save when in manual mode
        this.postConfig('FACILITY', this.getFacilityId, this.facility_NCP)
        this.postConfig('UTILITY', this.getUtilityId, this.utility_1CP)
        this.postConfig('ISO', this.getIsoId, this.iso_5CP)
      }
      this.postModeConfig()
      this.$toast.success("Your configurations have been saved.")
    },
    async getModeConfig() {
      try {
        let response = await ConfigService.getValue('PEAK_DAY_MODE')
        if (response) {
          if (response.data) {
            if (response.data.value === 'manual') {
              this.peak_mode = true
            } else if (response.data.value === 'auto') {
              this.peak_mode = false
            }
          }
        }
      } catch (error) {
        this.error = 'Cannot determine the override value.  Defaulting to auto.'
        this.peak_mode = false
      }
      this.checkManualOverrides()
    },
    postConfig(type, id, value = false) {
      let key = 'PEAK_DAY_FLAG_' + type + '_' + id
      ConfigService.postValue(key, value)
        // .then(response => {})
        .catch(error => { this.$toast.error("Error saving configuration " + error.response) } )
    },
    postModeConfig() {
      let mode = ''
      if (this.peak_mode === true) {
        mode = 'manual'
      } else if (this.peak_mode === false) {
        mode = 'auto'
      }
      let key = 'PEAK_DAY_MODE'
      ConfigService.postValue(key, mode)
        // .then(response => {})
        .catch(error => { this.$toast.error("Error saving configuration " + error.response) } )
    }
  }
}
</script>
